import { useState } from 'react';
import { Loader2, MapPin, MonitorPlay, Plus } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import { getOptimizedImageUrl } from '@/shared/lib/media/optimizeImageUrl';
import type { ScreenListItem } from '@/features/campaigns/components/ScreensStep/ScreensStep.types';

const PREVIEW_COUNT = 4;

type AddMoreScreensProps = {
  screens: ScreenListItem[];
  loading: boolean;
  /** Estimated price of one play on one day. */
  playPrice: (screen: ScreenListItem) => number;
  /** Cost of the first play on every campaign day. */
  days: number;
  remainingBudget: number | null;
  addingId: string | null;
  disabled: boolean;
  orientationLabel: string;
  onAdd: (screen: ScreenListItem) => void;
};

export function AddMoreScreens({
  screens,
  loading,
  playPrice,
  days,
  remainingBudget,
  addingId,
  disabled,
  orientationLabel,
  onAdd,
}: AddMoreScreensProps) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? screens : screens.slice(0, PREVIEW_COUNT);

  if (!loading && screens.length === 0) return null;

  return (
    <section className="camp-section text-left">
      <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">Add more screens</h2>
      <p className="mt-1 mb-0 text-xs text-[var(--dash-muted)]">
        Other {orientationLabel.toLowerCase()} screens in your cities. Prices here are estimates; the exact price shows once added.
      </p>

      {loading ? (
        <p className="mt-4 mb-0 inline-flex items-center gap-2 text-sm text-[var(--dash-muted)]" role="status">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading screens…
        </p>
      ) : (
        <ul className="m-0 mt-4 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2">
          {visible.map((screen) => {
            const id = String(screen._id ?? screen.id ?? '');
            const thumb = getOptimizedImageUrl(screen.thumbnail?.url || '', { width: 240 });
            const firstDay = playPrice(screen) * days;
            const tooExpensive = remainingBudget != null && firstDay > remainingBudget + 0.005;
            const adding = addingId === id;
            const place = [screen.address?.area, screen.address?.city].filter(Boolean).join(', ');

            return (
              <li key={id} className="flex items-center gap-3 rounded-2xl border border-[var(--dash-line)] bg-white p-2.5">
                <div className="h-14 w-16 shrink-0 overflow-hidden rounded-lg bg-[var(--dash-soft)]">
                  {thumb ? (
                    <img src={thumb} alt="" loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-[var(--dash-muted)]">
                      <MonitorPlay className="h-5 w-5 opacity-40" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate text-sm font-bold text-[var(--dash-ink)]">{screen.screenName || screen.name}</p>
                  <p className="m-0 mt-0.5 flex items-center gap-1 truncate text-xs text-[var(--dash-muted)]">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {place || '—'}
                  </p>
                  <p className="m-0 mt-0.5 text-xs font-semibold text-[var(--dash-ink)]">
                    from {formatMoney(firstDay)}
                    <span className="font-normal text-[var(--dash-muted)]"> / play a day</span>
                    {tooExpensive ? <span className="ml-1 font-normal text-red-600">· over budget</span> : null}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={disabled || adding}
                  onClick={() => onAdd(screen)}
                  title={tooExpensive ? 'This goes over your budget. You can pay the difference.' : undefined}
                  className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-[var(--dash-brand)]/40 px-2.5 py-1.5 text-xs font-semibold text-[var(--dash-brand)] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Add
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && screens.length > PREVIEW_COUNT ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 border-none bg-transparent p-0 text-xs font-semibold text-[var(--dash-brand)] hover:underline"
        >
          {showAll ? 'Show fewer' : `Show all ${screens.length}`}
        </button>
      ) : null}
    </section>
  );
}
